import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setModalState } from '../../stores/actions/modal';
import { MODAL_AVATAR_ACTIONS, MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { UploadImage, UploadResponse } from '../../connection/upload-image';
import { GreenroomState } from '../../stores/types/types';
import { showAlert } from '../../components/general-components/alert-service';
import { UploadVirtualBg } from '../../connection/media';
import {
	addUploadingVBG, loadVBGs, removeUploadingVBG, selectVBG,
} from '../../stores/actions/content';
import { OpenTokContext } from '../../contexts/opentok/opentok-context';
import { VirtualBg } from '../../stores/types/content';
import logger from '../../utils/logger-rum';

interface UseAddBackground {
	uploadNewVBG: (file: File) => void;
	createVbgFromMedia: (item: VirtualBg) => void;
}

const URL = window.URL || window.webkitURL;

const useAddBackground = (onAdd?: Function): UseAddBackground => {
	const dispatch = useDispatch();

	const { token, meeting, user } = useSelector((s: GreenroomState) => s.SessionReducer);

	const { opentok } = useContext(OpenTokContext);

	const meetingId = meeting?.meeting ?? 0;

	const openTrimModal = (existingInfo: VirtualBg, uploadKey: string) => {
		dispatch(removeUploadingVBG(uploadKey));
		dispatch(setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.AVATAR_PHOTO,
			modalData: {
				isOnboarding: false,
				modalAvatarAction: MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND,
				existingInfo,
			},
		}));
	};

	const onAddToLibrary = (url: string, uploadKey: string) => {
		if (!user?.gr_user || !token || !meetingId) return;

		dispatch(addUploadingVBG(uploadKey));

		UploadVirtualBg(token, user?.gr_user, meetingId, url)
			.then(() => {
				const newBg: VirtualBg = { url, blur: 0, uniqueId: `upload_${url}` };
				dispatch(loadVBGs(token, user.gr_user, url));
				dispatch(selectVBG(newBg));
				opentok?.enableVirtualBackground(newBg)
					.catch(console.error);
				onAdd?.();
			}).catch((e) => {
				logger.error(e);
				showAlert({
					message: 'Error adding image to library',
					description: 'We ran into an issue adding that image to the library. Please try again.',
					duration: 4000,
				});
			}).finally(() => {
				dispatch(removeUploadingVBG(uploadKey));
			});
	};

	const uploadImageDirectly = (file: File) => {
		const key = `${Math.random() * Math.random()}_uploaded`;
		dispatch(addUploadingVBG(key));
		UploadImage(token, file, true).then((res: UploadResponse) => {
			if (res.url) {
				onAddToLibrary(res.url, key);
			}
		}).catch(() => {
			showAlert({
				message: 'Upload Error',
				description: 'Something went wrong choosing a file, please try again.',
				duration: 4000,
				type: 'error',
			});
			dispatch(removeUploadingVBG(key));
		});
	};

	const catchSixteenByNine = (existingInfo: VirtualBg, freshFile?: File) => {
		const key = `${Math.random() * Math.random()}_uploaded`;
		const { url } = existingInfo;
		if (url) {
			const img = new Image();

			img.onload = () => {
				// ...or close enough to 16:9...
				const sixteenByNine = (
					((img.width / img.height) > 1.74)
					&& ((img.width / img.height) < 1.8)
				);
				if (sixteenByNine) {
					if (freshFile) {
						uploadImageDirectly(freshFile);
					} else {
						onAddToLibrary(url, key);
					}
				} else {
					openTrimModal(existingInfo, key);
				}
			};
			img.src = url;
		}
	};

	const createVbgFromMedia = (item: VirtualBg) => {
		catchSixteenByNine(item);
	};

	const uploadNewVBG = (file: File) => {
		const objectUrl = URL.createObjectURL(file);
		const newBg = { url: objectUrl, fileType: file.type, name: file.name } as VirtualBg;
		catchSixteenByNine(newBg, file);
	};

	return {
		uploadNewVBG,
		createVbgFromMedia,
	};
};

export default useAddBackground;
